import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Briefcase,
  Calendar,
  Check,
  Clock,
  DollarSign,
  ExternalLink,
  Globe,
  MapPin,
  PiggyBank,
  Quote,
  ShieldCheck,
  TrendingUp,
  Users,
  XCircle,
  Zap,
} from 'lucide-react';
import CaseStudiesLayout from './CaseStudiesLayout';
import CaseStudyLanguageLabel from './CaseStudyLanguageLabel';
import {
  CASE_STUDY_LOCALES,
  DEFAULT_CASE_STUDY_LOCALE,
  caseStudyLocaleNames,
  caseStudyUi,
  getAlternateCaseStudyPath,
  getCaseStudyPath,
  getLocalizedCaseStudy,
  getPreferredCaseStudyLocale,
  hasCaseStudyTranslation,
  isSupportedCaseStudyLocale,
  setCaseStudyLocaleOverride,
} from '../../data/caseStudies';
import { scrollToTop } from '../../utils/scrollUtils';

const iconMap = {
  'chart-down': BarChart3,
  'chart-line': BarChart3,
  'calendar-old': Calendar,
  'money-lost': DollarSign,
  'piggy-bank': PiggyBank,
  'shield-off': ShieldCheck,
  'trending-up': TrendingUp,
  users: Users,
  x: XCircle,
  zap: Zap,
};

const getIcon = (icon) => iconMap[icon] || BarChart3;

const formatDate = (date, locale) => {
  if (!date) return null;

  return new Intl.DateTimeFormat(locale === 'sk' ? 'sk-SK' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date));
};

const getHost = (url) => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

const FactRow = ({ icon: Icon, label, children }) => (
  <li className="case-study-fact-row">
    <span>
      <Icon className="h-4 w-4" />
      {label}
    </span>
    <strong>{children}</strong>
  </li>
);

const StatGrid = ({ stats }) => {
  if (!stats?.length) return null;

  return (
    <div className="case-study-stat-grid">
      {stats.map((stat) => {
        const Icon = getIcon(stat.icon);
        return (
          <div key={`${stat.label}-${stat.value}`} className="case-study-stat-card">
            <Icon className="h-5 w-5 text-indigo-300" />
            <div className="case-study-stat-value">{stat.value}</div>
            <div className="case-study-stat-label">{stat.label}</div>
          </div>
        );
      })}
    </div>
  );
};

const MetricGrid = ({ metrics }) => {
  if (!metrics?.length) return null;

  return (
    <div className="case-study-metric-grid">
      {metrics.map((metric) => {
        const Icon = getIcon(metric.icon);
        return (
          <div key={`${metric.label}-${metric.value}`} className="case-study-metric-card">
            <div className="case-study-metric-icon">
              <Icon className="h-5 w-5" />
            </div>
            <div className="case-study-metric-value">{metric.value}</div>
            <div className="case-study-metric-label">{metric.label}</div>
            {metric.description && (
              <p className="case-study-metric-description">{metric.description}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

const QuoteBlock = ({ quote, author }) => {
  if (!quote) return null;

  return (
    <blockquote className="case-study-quote">
      <Quote className="case-study-quote-icon" />
      <p>{quote}</p>
      {author && <footer>{author}</footer>}
    </blockquote>
  );
};

const BrowserImage = ({ src, alt, caption, label }) => {
  if (!src) return null;

  return (
    <figure className="case-study-browser">
      <div className="case-study-browser-top">
        <span />
        <span />
        <span />
        {label && <strong>{label}</strong>}
      </div>
      <img src={src} alt={alt} loading="lazy" />
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
};

const BulletList = ({ items }) => {
  if (!items?.length) return null;

  return (
    <ul className="case-study-check-list">
      {items.map((item) => (
        <li key={item}>
          <Check className="h-4 w-4" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

const LanguageSwitcher = ({ study, locale, navigate }) => (
  <div className="case-study-language">
    <div className={`case-study-language-options active-${locale}`}>
      {CASE_STUDY_LOCALES.map((targetLocale) => {
        const path = getAlternateCaseStudyPath(study, targetLocale);
        const isActive = targetLocale === locale;
        const isDisabled = !hasCaseStudyTranslation(study, targetLocale);

        return (
          <button
            key={targetLocale}
            type="button"
            className={`case-study-language-option ${isActive ? 'active' : ''}`}
            disabled={isDisabled}
            aria-pressed={isActive}
            title={caseStudyLocaleNames[targetLocale]}
            onClick={() => {
              if (path && !isActive) {
                setCaseStudyLocaleOverride(targetLocale);
                navigate(path, { preventScrollReset: true });
              }
            }}
          >
            <CaseStudyLanguageLabel locale={targetLocale} />
          </button>
        );
      })}
    </div>
  </div>
);

const CaseStudyNotFound = ({ locale, ui, isMenuOpen, setIsMenuOpen }) => (
  <CaseStudiesLayout
    title={ui.notFoundTitle}
    description={ui.notFoundDescription}
    locale={locale}
    isMenuOpen={isMenuOpen}
    setIsMenuOpen={setIsMenuOpen}
  >
    <section className="case-study-not-found">
      <div className="case-study-bg-glow" aria-hidden="true" />
      <div className="case-studies-container">
        <Link to={getCaseStudyPath(locale)} className="case-study-back-link">
          <ArrowLeft className="h-4 w-4" />
          {ui.backToStudies}
        </Link>
        <div className="case-study-empty">
          <div className="case-study-empty-icon">
            <Briefcase className="h-8 w-8" />
          </div>
          <h1>{ui.notFoundTitle}</h1>
          <p>{ui.notFoundDescription}</p>
          <Link to={getCaseStudyPath(locale)} className="premium-btn inline-flex items-center justify-center">
            {ui.backToStudies}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  </CaseStudiesLayout>
);

const CaseStudyDetail = ({ isMenuOpen, setIsMenuOpen }) => {
  const { locale = DEFAULT_CASE_STUDY_LOCALE, slug } = useParams();
  const navigate = useNavigate();
  const isSupportedLocale = isSupportedCaseStudyLocale(locale);
  const activeLocale = isSupportedLocale ? locale : DEFAULT_CASE_STUDY_LOCALE;
  const ui = caseStudyUi[activeLocale];
  const study = getLocalizedCaseStudy(slug, activeLocale);

  useEffect(() => {
    const preferredLocale = getPreferredCaseStudyLocale();

    if (
      (!isSupportedLocale || activeLocale !== preferredLocale)
      && getLocalizedCaseStudy(slug, preferredLocale)
    ) {
      navigate(getCaseStudyPath(preferredLocale, slug), {
        replace: !isSupportedLocale,
        preventScrollReset: true,
      });
    }
  }, [activeLocale, isSupportedLocale, navigate, slug]);

  useEffect(() => {
    scrollToTop('auto');
  }, [slug]);

  if (!study) {
    return (
      <CaseStudyNotFound
        locale={activeLocale}
        ui={ui}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      />
    );
  }

  const { content } = study;
  const host = getHost(study.url);
  const imageAlt = content.imageAlt || study.imageAlt || content.tagline;
  const industry = content.industry || study.industry;
  const location = content.location || study.location;

  return (
    <CaseStudiesLayout
      title={content.metaTitle || study.clientName}
      description={content.metaDescription || content.tagline}
      locale={activeLocale}
      ogType="article"
      isMenuOpen={isMenuOpen}
      setIsMenuOpen={setIsMenuOpen}
    >
      <article className="case-study-detail">
        <div className="case-study-bg-glow" aria-hidden="true" />
        <div className="case-studies-container">
          <div className="case-study-detail-grid">
            <div className="case-study-content-column">
              <Link to={getCaseStudyPath(activeLocale)} className="case-study-back-link">
                <ArrowLeft className="h-4 w-4" />
                {ui.backToStudies}
              </Link>

              <figure className="case-study-hero-image">
                {study.image ? (
                  <img src={study.image} alt={imageAlt} />
                ) : (
                  <div className="case-study-hero-placeholder" aria-hidden="true" />
                )}
              </figure>

              <div className="case-study-date-row">
                {study.publishedDate && (
                  <span>
                    {ui.published}: {formatDate(study.publishedDate, activeLocale)}
                  </span>
                )}
                {study.lastModified && study.lastModified !== study.publishedDate && (
                  <span>
                    {ui.updated}: {formatDate(study.lastModified, activeLocale)}
                  </span>
                )}
              </div>

              <header className="case-study-article-header">
                <h1>{content.tagline}</h1>
                {content.summary && <p>{content.summary}</p>}
              </header>

              <div className="case-study-prose">
                {content.challenge && (
                  <section>
                    <h2>{content.challenge.title}</h2>
                    <p>{content.challenge.description}</p>
                    <BulletList items={content.challenge.problems} />
                    <QuoteBlock quote={content.challenge.quote} author={content.challenge.quoteAuthor} />
                    <StatGrid stats={content.challenge.stats} />
                    <BrowserImage
                      src={study.beforeImage}
                      alt={`${study.clientName} before`}
                      label={ui.challengeStats}
                      caption={content.beforeImageCaption}
                    />
                  </section>
                )}

                {content.solution && (
                  <section>
                    <h2>{content.solution.title}</h2>
                    <p>{content.solution.description}</p>
                    <BulletList items={content.solution.features} />
                    {content.solution.timeline && (
                      <div className="case-study-timeline">
                        <Clock className="h-5 w-5" />
                        <span>{ui.timeline}</span>
                        <strong>{content.solution.timeline}</strong>
                      </div>
                    )}
                  </section>
                )}

                {content.results && (
                  <section>
                    <h2>{content.results.title}</h2>
                    <p>{content.results.description}</p>
                    <BrowserImage
                      src={study.afterImage}
                      alt={`${study.clientName} after`}
                      label={content.afterImageLabel || ui.additionalResults}
                      caption={content.afterImageCaption}
                    />
                    <MetricGrid metrics={content.results.metrics} />
                    <BulletList items={content.results.additionalResults} />
                    <QuoteBlock quote={content.results.quote} author={content.results.quoteAuthor} />
                  </section>
                )}

                {content.solution?.technologies?.length > 0 && (
                  <section className="case-study-tech-section">
                    <h2>{ui.technologies}</h2>
                    <div className="case-study-tech-list">
                      {content.solution.technologies.map((tech) => (
                        <span key={tech}>{tech}</span>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </div>

            <aside className="case-study-sidebar">
              <div className="case-study-sidebar-card">
                <LanguageSwitcher study={study} locale={activeLocale} navigate={navigate} />

                <div className="case-study-sidebar-client">
                  <h2>{study.clientName}</h2>
                  <Link to="/contact" className="premium-btn inline-flex items-center justify-center">
                    {ui.similarProject}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>

                <ul className="case-study-facts">
                  <FactRow icon={Briefcase} label={ui.client}>
                    {study.clientName}
                  </FactRow>
                  {study.url && (
                    <li className="case-study-fact-row">
                      <span>
                        <Globe className="h-4 w-4" />
                        {ui.website}
                      </span>
                      <a href={study.url} target="_blank" rel="noopener noreferrer">
                        {host}
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </li>
                  )}
                  {industry && (
                    <FactRow icon={Briefcase} label={ui.industry}>
                      {industry}
                    </FactRow>
                  )}
                  {location && (
                    <FactRow icon={MapPin} label={ui.location}>
                      {location}
                    </FactRow>
                  )}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </article>
    </CaseStudiesLayout>
  );
};

export default CaseStudyDetail;
